import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useMemo } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Box, Divider, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import DatePicker from '@Components/DatePicker';
import SBDropdown from '@Components/Dropdown';

import { ShipmentObj } from '@Reducers/Invoice/Type';

import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import { NonAccountInvConfig, TaxInformationOption, splitTaxOptions } from '../utils/nonAccountInvoiceUtils';

export interface NonAccountConfigSectionProps {
  config: NonAccountInvConfig;
  setConfig: React.Dispatch<React.SetStateAction<NonAccountInvConfig>>;
  selectedShipments: ShipmentObj[];
  totalSelectedAmount: number;
  taxInformationOptions: TaxInformationOption[];
  sacCodeOptions: { display: string; value: string }[];
  orgName?: string;
  isWyngsMy: boolean;
  loadingD365: boolean;
  primaryCurrencySymbol: string;
  onSendTo365: () => void;
  onRemoveShipment: (id: string) => void;
}

const NonAccountConfigSection = React.memo(function NonAccountConfigSection(props: NonAccountConfigSectionProps) {
  const {
    config,
    setConfig,
    selectedShipments,
    totalSelectedAmount,
    taxInformationOptions,
    sacCodeOptions,
    orgName,
    isWyngsMy,
    loadingD365,
    primaryCurrencySymbol,
    onSendTo365,
    onRemoveShipment,
  } = props;

  const theme = useTheme();
  const locale = useLocale();

  const handleInvoiceDateChange = useCallback(
    (date: Dayjs) => {
      setConfig(prev => ({
        ...prev,
        invoiceDate: date,
        dueDate: prev.dueDate && prev.dueDate.isBefore(date) ? date : prev.dueDate,
      }));
    },
    [setConfig],
  );

  const handleDueDateChange = useCallback(
    (date: Dayjs) => {
      setConfig(prev => ({
        ...prev,
        dueDate: date,
      }));
    },
    [setConfig],
  );

  const isD365Disabled = useMemo(() => {
    return !config.invoiceDate || !config.dueDate || selectedShipments.length === 0 || loadingD365;
  }, [config.invoiceDate, config.dueDate, selectedShipments.length, loadingD365]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Top Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5, flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TuneOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.1 }}>
              Invoice Parameters
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
              Configure dates, taxation, and review selected shipments
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Scrollable Form & Preview Area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Date Pickers Card */}
        <Box
          sx={{
            p: 1.75,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary', mb: 1.25 }}>
            DATES & BILLING PERIOD
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                required
                label="Invoice Date"
                value={config.invoiceDate}
                handleChange={handleInvoiceDateChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                required
                label="Due Date"
                value={config.dueDate}
                handleChange={handleDueDateChange}
                minDate={config.invoiceDate ? dayjs(config.invoiceDate) : undefined}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Taxation Controls (if orgName !== CSN) */}
        {orgName !== 'Cargo Solutions Network' && (
          <Box
            sx={{
              p: 1.75,
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary', mb: 1.25 }}>
              TAXATION & COMPLIANCE
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <SBDropdown
                  label="Select Taxation"
                  id="taxBreakdown"
                  placeholder="Select"
                  options={taxInformationOptions.map(t => ({
                    display: t.display,
                    value: String(t.value),
                    key: t.key,
                  }))}
                  onChange={value => {
                    setConfig(prev => ({ ...prev, taxBreakdown: value }));
                  }}
                  name="taxBreakdown"
                  value={config.taxBreakdown || []}
                />
              </Grid>
              <Grid item xs={12}>
                <SBDropdown
                  label="Tax Breakdown"
                  id="splitTax"
                  placeholder="Select"
                  options={splitTaxOptions}
                  onChange={value => {
                    setConfig(prev => ({ ...prev, splitTax: value }));
                  }}
                  name="splitTax"
                  value={config.splitTax || []}
                />
              </Grid>
              <Grid item xs={12}>
                <SBDropdown
                  label="SAC Code"
                  id="sacCode"
                  placeholder="Select"
                  options={sacCodeOptions}
                  onChange={value => {
                    setConfig(prev => ({ ...prev, sacCode: value?.[0] }));
                  }}
                  name="sacCode"
                  value={config.sacCode ? [config.sacCode] : []}
                />
              </Grid>
              {!isWyngsMy && (
                <Grid item xs={12}>
                  <SbButton
                    variant="outlined"
                    fullWidth
                    disabled={isD365Disabled}
                    loading={loadingD365}
                    onClick={onSendTo365}
                    startIcon={<SendOutlinedIcon fontSize="small" />}
                    sx={{
                      borderRadius: '8px',
                      height: 40,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Send to D365
                  </SbButton>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Selected Shipments Preview List */}
        <Box
          sx={{
            flex: 1,
            minHeight: 180,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            p: 1.75,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>
              SELECTED SHIPMENTS PREVIEW
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: 'primary.main' }}>
              {selectedShipments.length} {selectedShipments.length === 1 ? 'Item' : 'Items'}
            </Typography>
          </Stack>

          {selectedShipments.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: '8px',
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                No shipments selected yet. Check shipments on the left to include them in this invoice.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: 250,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pr: 0.5,
              }}
            >
              {selectedShipments.map((shipment, index) => {
                const amt = Number(shipment.totalAmount ?? shipment.sellingPricing?.totalPricing ?? 0);
                return (
                  <Box
                    key={shipment._id}
                    sx={{
                      p: 1,
                      borderRadius: '8px',
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>
                          #{index + 1}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
                          {shipment.shipmentNumber}
                        </Typography>
                      </Stack>
                      <Typography noWrap sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {shipment._userId?.name || '-'} •{' '}
                        {shipment._chargingSystemId?.name || (shipment as any)?.system?.name || '-'}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'primary.main' }}>
                        {primaryCurrencySymbol} {formatNumber(amt, locale)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onRemoveShipment(shipment._id)}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' }, p: 0.5 }}
                        aria-label={`Remove shipment ${shipment.shipmentNumber}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Financial Summary Callout with mb: 2.5 gap */}
        <Box
          sx={{
            p: 1.75,
            borderRadius: '12px',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 2.5,
          }}
        >
          <Stack spacing={0.75}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500 }}>
                Selected Shipments:
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
                {selectedShipments.length}
              </Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: 'text.primary' }}>
                Total Invoice Amount:
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: 'primary.main' }}>
                {primaryCurrencySymbol} {formatNumber(totalSelectedAmount, locale)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
});

export default NonAccountConfigSection;
